import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmRejectReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmRejectReasonTypeSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import {
  APPROVE_ORDER_NUMBER_BLOCK_ID,
  DECISION_PLACE_BLOCK_ID,
  SIGNER_FULL_NAME_BLOCK_ID,
  SIGNER_POSITION_BLOCK_ID,
  getRejectReasonBlockId,
  REJECT_REASON_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { inspectionDecisionOptions } from 'KnmPackage/MainModule/constants/inspectionDecisionOptions';
import { InspectionDecisionsEnum } from 'KnmPackage/MainModule/enums/InspectionDecisionsEnum';
import { IInspectionDecisionSelectOption } from 'KnmPackage/MainModule/interfaces/IInspectionDecisionSelectOption';
import { getKnmFormRef, getKnmRejectReasonTypesSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErpFormRejectReason } from '../interfaces/IKnmErpFormRejectReason';
import styles from './KnmErpApproveInfoDetails.module.scss';

interface IKnmErpApproveInfoDetailsProps {
  approveRequired: boolean | undefined;
  inspectionDecisionId: IInspectionDecisionSelectOption['id'] | undefined;
  planId: IKnm['planId'] | undefined;
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnly: boolean;
}

interface IKnmErpApproveInfoDetailsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmRejectReasonTypesSelectOptions: IKnmRejectReasonTypeSelectOption[] | undefined;
}

const KnmErpApproveInfoDetailsWithoutStore = ({
  approveRequired,
  inspectionDecisionId,
  planId,
  typeCode,
  watchOnly,
  formRef,
  knmRejectReasonTypesSelectOptions,
}: IKnmErpApproveInfoDetailsProps & IKnmErpApproveInfoDetailsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isPlanType = typeCode === KnmTypeCodesEnum.PP;

  const addRejectReason = useCallback(() => !watchOnly && push && push('rejectReasons', { listId: uuidv4() }), [
    push,
    watchOnly,
  ]);

  const makeRemoveRejectReasonHandler = (index: number) => () => !watchOnly && remove && remove('rejectReasons', index);

  const handleInspectionDecisionSelectChange = useCallback(
    (activeOption?: IInspectionDecisionSelectOption) => {
      if (activeOption?.id !== InspectionDecisionsEnum.DECLINED) {
        formRef?.current.change('rejectReasons');
      }
    },
    [formRef],
  );

  return (
    <Row title="Сведения о согласовании проведения КНМ с органами прокуратуры">
      {approveRequired ? (
        <>
          <Row>
            <FieldBlock
              label={isPlanType ? 'Включено в сводный план' : 'Решение по заявлению'}
              size="half"
              color="black"
            >
              <SelectInputField
                name="inspectionDecision"
                options={inspectionDecisionOptions}
                size="medium"
                disabled={watchOnly}
                onChange={handleInspectionDecisionSelectChange}
              />
            </FieldBlock>
            <FieldBlock label="Номер приказа" id={APPROVE_ORDER_NUMBER_BLOCK_ID} color="black" size="col-4">
              <TextInputField name="approveOrderNumber" disabled={watchOnly} />
            </FieldBlock>
            <FieldBlock label="Дата приказа" color="black" size="col-4">
              <DatePickerField name="approveOrderDate" disabled={watchOnly} />
            </FieldBlock>
          </Row>

          {inspectionDecisionId === InspectionDecisionsEnum.DECLINED && (
            <Row>
              <FieldBlock
                label="Основание отказа"
                color="black"
                button={
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    variant="link"
                    color="primary"
                    type="button"
                    disabled={watchOnly}
                    onClick={addRejectReason}
                  />
                }
                id={REJECT_REASON_BLOCK_ID}
              >
                <FieldArray name="rejectReasons">
                  {({ fields }) => {
                    if (!fields.length) {
                      return null;
                    }

                    return fields.map((name, index) => {
                      const fieldValue: IKnmErpFormRejectReason = fields.value?.[index];
                      const id = getRejectReasonBlockId(fieldValue.listId);

                      return (
                        <Row key={id} id={id} notWrap isListItem margin="medium">
                          <Row>
                            <Row>
                              <FieldBlock>
                                <SelectInputField
                                  name={`${name}.type`}
                                  placeholder="Выберите основание отказа"
                                  options={knmRejectReasonTypesSelectOptions}
                                  disabled={watchOnly}
                                  size="medium"
                                />
                              </FieldBlock>
                            </Row>
                            <FieldBlock>
                              {fieldValue?.type?.hasText && (
                                <TextareaField
                                  name={`${name}.text`}
                                  placeholder="Введите иную причину отказа"
                                  disabled={watchOnly}
                                />
                              )}
                            </FieldBlock>
                          </Row>
                          <CloseButton
                            className="shared-deleting-row-btn"
                            onClick={makeRemoveRejectReasonHandler(index)}
                          />
                        </Row>
                      );
                    });
                  }}
                </FieldArray>
              </FieldBlock>
            </Row>
          )}

          <Row>
            <FieldBlock
              label="Дата вынесения решения о согласовании проведения КНМ"
              placementLabel="left"
              color="black"
              type="not-space"
            >
              <DatePickerField className={styles.Field} name="decisionDate" disabled={watchOnly} />
            </FieldBlock>
          </Row>

          <Row>
            <FieldBlock label="Место вынесения решения" id={DECISION_PLACE_BLOCK_ID} color="black">
              <KnmAddressField id="decisionPlace" addressFieldPathWithPrefix="decisionPlace" disabled={watchOnly} />
            </FieldBlock>
          </Row>
          <Row margin={typeof planId !== 'number' || !isPlanType ? 'none' : undefined}>
            <FieldBlock label="ФИО подписанта" id={SIGNER_FULL_NAME_BLOCK_ID} color="black" size="half">
              <TextareaField name="signerFullName" disabled={watchOnly} />
            </FieldBlock>
            <FieldBlock label="Должность подписанта" id={SIGNER_POSITION_BLOCK_ID} color="black" size="half">
              <TextareaField name="signerPosition" disabled={watchOnly} />
            </FieldBlock>
          </Row>

          {typeof planId === 'number' && (
            <Row>
              <FieldBlock
                label="Проверка включена в ежегодный сводный план №"
                placementLabel="left"
                type="not-space"
                color="black"
              >
                <TextInput
                  className={styles.ApproveInfoPlanNumberField}
                  id="approveInfoPlanNumber"
                  name="approveInfoPlanNumber"
                  value={planId}
                  disabled
                />
              </FieldBlock>
            </Row>
          )}

          {isPlanType && (
            <Row
              title="Информация о постановлении о назначении административного наказания или решении о приостановлении и
            (или) аннулировании лицензии"
            >
              <Row>
                <FieldBlock
                  label="Постановление о назначении административного наказания или решении о приостановлении и (или)
                    аннулировании лицензии"
                  placementLabel="left"
                  color="black"
                >
                  <TextInputField className="shared-row-field" name="licenseReason" disabled={watchOnly} />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock label="Дата вступления в законную силу" placementLabel="left" color="black">
                  <DatePickerField className="shared-row-field" name="licenseReasonStartDate" disabled={watchOnly} />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock
                  label="Дата окончания проведения КНМ, по результатам которого они приняты"
                  placementLabel="left"
                  color="black"
                >
                  <DatePickerField className="shared-row-field" name="licenseReasonKnmEndDate" disabled={watchOnly} />
                </FieldBlock>
              </Row>
            </Row>
          )}
        </>
      ) : (
        <p className={styles.Notification}>
          С указанным основанием регистрации КНМ согласование с органом прокуратуры не требуется
        </p>
      )}
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpApproveInfoDetailsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmRejectReasonTypesSelectOptions: getKnmRejectReasonTypesSelectOptions(state),
});

export const KnmErpApproveInfoDetails = connect(mapStateToProps)(KnmErpApproveInfoDetailsWithoutStore);
